/**
 * Portfolio view (handoff §5.1) — main tabs, search, filters drawer, artifact grid.
 *
 * Filtering is done here against the fixture list. The handoff says the server applies
 * filters (§5.1: "Server applies", search "debounced 200ms … server-side"); when the §4.1
 * endpoint lands, `onQueryChange` becomes the fetch trigger and this local filtering is
 * what renders until it resolves.
 */

import { useMemo, useState } from 'react';
import { Search, SlidersHorizontal, X, Trash2 } from 'lucide-react';
import { color, font, radius, shadow, OUTCOME_ORDER, OUTCOME_LABEL } from '../tokens';
import type { ArtifactList, MainTab } from '../types';
import { OutcomePill, ComplexityBar, StatusPill, CatChip } from '../components/Pills';

// Single taxonomy (task 1218070214136694): All + the six engine outcomes (Box 1-4 / Consolidate /
// Decommission / Queue) — the same words Framework Mapping and the Review Queue use.
const TABS: { id: MainTab; label: string }[] = [
  { id: 'all', label: 'All' },
  ...OUTCOME_ORDER.map((o) => ({ id: o as MainTab, label: OUTCOME_LABEL[o] })),
];

const OWNERS = ['any', 'CFO', 'CRO', 'Treasury'] as const;
const COMPLEXITIES = ['any', 'High', 'Medium', 'Low'] as const;
const CATS = ['any', '1', '2', '3'] as const;

interface Props {
  list: ArtifactList;
  mainTab: MainTab;
  onMainTab: (t: MainTab) => void;
  selectedArtifactId: string | null;
  onOpenArtifact: (id: string) => void;
  // Round 2 UX fix — a trash icon appears on row hover so a consultant can remove one mis-uploaded
  // or duplicate document without re-ingesting the whole inventory. Optional so any test/storybook
  // usage of this grid without a delete handler just doesn't render the icon.
  onDeleteArtifact?: (id: string) => void;
}

export default function Portfolio({ list, mainTab, onMainTab, selectedArtifactId, onOpenArtifact, onDeleteArtifact }: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<(typeof OWNERS)[number]>('any');
  const [complexFilter, setComplexFilter] = useState<(typeof COMPLEXITIES)[number]>('any');
  const [catFilter, setCatFilter] = useState<(typeof CATS)[number]>('any');
  const [confidenceMin, setConfidenceMin] = useState(0);

  const activeFilterCount =
    (ownerFilter !== 'any' ? 1 : 0) + (complexFilter !== 'any' ? 1 : 0) +
    (catFilter !== 'any' ? 1 : 0) + (confidenceMin > 0 ? 1 : 0);

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return list.items.filter((r) => {
      if (mainTab !== 'all' && r.outcome !== mainTab) return false;
      if (q && !`${r.name} ${r.meta_line}`.toLowerCase().includes(q)) return false;
      if (ownerFilter !== 'any' && !r.owner.startsWith(ownerFilter)) return false;
      if (complexFilter !== 'any' && r.complexity !== complexFilter) return false;
      if (catFilter !== 'any' && String(r.timing_cat) !== catFilter) return false;
      if ((r.confidence ?? 0) * 100 < confidenceMin) return false;
      return true;
    });
  }, [list.items, mainTab, searchQuery, ownerFilter, complexFilter, catFilter, confidenceMin]);

  const counts = list.counts_by_outcome;
  const tabCount = (id: MainTab) => (id === 'all' ? list.total : counts[id as keyof typeof counts] ?? 0);

  const clearFilters = () => {
    setOwnerFilter('any'); setComplexFilter('any'); setCatFilter('any'); setConfidenceMin(0);
  };

  return (
    <div>
      {/* Tabs + search + filter toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', gap: 4, background: '#eceef7', padding: 4, borderRadius: 12 }}>
          {TABS.map((t) => {
            const active = mainTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => onMainTab(t.id)}
                style={{
                  padding: '8px 14px', border: 'none', borderRadius: 9, cursor: 'pointer',
                  fontFamily: font.ui, fontSize: 13, fontWeight: 600,
                  background: active ? color.surface : 'transparent',
                  color: active ? color.ink : color.textMuted,
                  boxShadow: active ? '0 1px 3px #0d0b2b18' : 'none',
                }}
              >
                {t.label}
                <span style={{ marginLeft: 7, fontSize: 12, color: active ? color.accent : '#9998b3', fontWeight: 700 }}>
                  {tabCount(t.id)}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9998b3' }} />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search artifacts…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 34px',
              border: `1px solid ${color.cardBorder}`, borderRadius: radius.control,
              fontFamily: font.ui, fontSize: 13.5, color: color.ink, background: color.surface, outline: 'none',
            }}
          />
        </div>

        <button
          onClick={() => setShowFilters((s) => !s)}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '10px 14px', cursor: 'pointer',
            border: `1px solid ${showFilters ? color.accent : color.cardBorder}`, borderRadius: radius.control,
            background: showFilters ? '#f5f7ff' : color.surface, color: showFilters ? color.accent : color.ink,
            fontFamily: font.ui, fontSize: 13, fontWeight: 600,
          }}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ background: color.accent, color: '#fff', borderRadius: 999, padding: '1px 6px', fontSize: 11 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Filters drawer */}
      {showFilters && (
        <div
          style={{
            background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: radius.card,
            padding: 16, marginBottom: 14, boxShadow: shadow.card,
            display: 'flex', gap: 26, flexWrap: 'wrap', alignItems: 'flex-end',
          }}
        >
          <ChipGroup label="Owner" options={OWNERS} value={ownerFilter} onChange={setOwnerFilter} />
          <ChipGroup label="Complexity" options={COMPLEXITIES} value={complexFilter} onChange={setComplexFilter} />
          <ChipGroup label="Timing" options={CATS} value={catFilter} onChange={setCatFilter} format={(o) => (o === 'any' ? 'Any' : `Cat ${o}`)} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: color.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Min confidence · {confidenceMin}%
            </div>
            <input
              type="range" min={0} max={100} value={confidenceMin}
              onChange={(e) => setConfidenceMin(Number(e.target.value))}
              style={{ width: 160, accentColor: color.accent }}
            />
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none',
                color: color.textMuted, fontFamily: font.ui, fontSize: 12.5, cursor: 'pointer', padding: '6px 0',
              }}
            >
              <X size={13} /> Clear
            </button>
          )}
        </div>
      )}

      <div style={{ fontSize: 12.5, color: color.textMuted, marginBottom: 10 }}>
        Showing {rows.length} of {list.total}
      </div>

      {/* Grid */}
      <div style={{ background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: radius.card, boxShadow: shadow.card, overflow: 'hidden' }}>
        {rows.map((r, i) => {
          const isSelected = r.id === selectedArtifactId;
          const isHovered = hoveredId === r.id;
          const isConfirming = confirmDeleteId === r.id;
          return (
            <div
              key={r.id}
              onClick={() => onOpenArtifact(r.id)}
              onMouseEnter={() => setHoveredId(r.id)}
              onMouseLeave={() => { setHoveredId((h) => (h === r.id ? null : h)); if (isConfirming) setConfirmDeleteId(null); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenArtifact(r.id); } }}
              style={{
                position: 'relative',
                display: 'grid', gridTemplateColumns: onDeleteArtifact ? 'minmax(0,2.4fr) 150px 140px 110px 90px 32px' : 'minmax(0,2.4fr) 150px 140px 110px 90px',
                gap: 14, alignItems: 'center', padding: '13px 16px', cursor: 'pointer',
                borderTop: i === 0 ? 'none' : `1px solid ${color.cardBorder}`,
                background: isSelected ? color.rowSelected : color.surface,
                boxShadow: isSelected ? `inset 3px 0 0 ${color.accent}` : 'none',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, color: color.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.name}
                  </span>
                  <CatChip cat={r.timing_cat} />
                </div>
                <div style={{ fontSize: 12, color: color.textMuted, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.meta_line}
                </div>
              </div>
              <OutcomePill value={r.outcome} confidence={r.confidence ?? undefined} />
              <ComplexityBar value={r.complexity} pct={r.complexity_pct} />
              <div style={{ fontSize: 12, color: color.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.assignee}
              </div>
              <StatusPill value={r.status} />
              {onDeleteArtifact && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {isConfirming ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); onDeleteArtifact(r.id); }}
                      title={`Confirm delete "${r.name}"`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
                        border: 'none', borderRadius: 6, cursor: 'pointer', background: '#fde2e1', color: '#9b1c1c',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(r.id); }}
                      title={`Delete "${r.name}"`}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 26,
                        border: 'none', borderRadius: 6, cursor: 'pointer', background: 'transparent',
                        color: color.textMuted, visibility: isHovered ? 'visible' : 'hidden',
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {rows.length === 0 && (
          <div style={{ padding: '40px 16px', textAlign: 'center', color: color.textMuted, fontSize: 13.5 }}>
            No artifacts match these filters.
          </div>
        )}
      </div>
    </div>
  );
}

function ChipGroup<T extends string>({
  label, options, value, onChange, format,
}: {
  label: string; options: readonly T[]; value: T; onChange: (v: T) => void; format?: (o: T) => string;
}) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: color.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              onClick={() => onChange(o)}
              style={{
                padding: '5px 11px', borderRadius: radius.chip, cursor: 'pointer',
                border: `1px solid ${active ? color.accent : color.cardBorder}`,
                background: active ? '#f5f7ff' : color.surface,
                color: active ? color.accent : color.textMuted,
                fontFamily: font.ui, fontSize: 12, fontWeight: 600,
              }}
            >
              {format ? format(o) : o === 'any' ? 'Any' : o}
            </button>
          );
        })}
      </div>
    </div>
  );
}
